import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Crown, Plus, X, User, Clock, Loader2, Check, Trophy, ChevronLeft,
  RefreshCw, Coins, Flag, Eye, EyeOff, ArrowUp, Minus,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { timeAgo } from '../lib/utils';
import BCHTipButton from '../components/BCHTipButton';

// ── Poker Constants ──────────────────────────────────────────────
const POKER_TAG = 'bchnostr-poker';
const STARTING_CHIPS = 1000;
const SMALL_BLIND = 10;
const BIG_BLIND = 20;

// ── Card Types ───────────────────────────────────────────────────
type Suit = 'h' | 'd' | 'c' | 's';
type Card = { rank: number; suit: Suit };
// rank: 2-14 (11=J, 12=Q, 13=K, 14=A)

const SUITS: Suit[] = ['h', 'd', 'c', 's'];
const SUIT_DISPLAY: Record<Suit, string> = { h: '\u2665', d: '\u2666', c: '\u2663', s: '\u2660' };
const SUIT_COLOR: Record<Suit, string> = { h: 'text-red-500', d: 'text-red-500', c: 'text-foreground', s: 'text-foreground' };
const RANK_DISPLAY: Record<number, string> = { 2:'2',3:'3',4:'4',5:'5',6:'6',7:'7',8:'8',9:'9',10:'10',11:'J',12:'Q',13:'K',14:'A' };

function cardToString(c: Card): string { return `${RANK_DISPLAY[c.rank]}${SUIT_DISPLAY[c.suit]}`; }
function cardKey(c: Card): string { return `${c.rank}${c.suit}`; }

// ── Deck & Shuffle ───────────────────────────────────────────────
function createDeck(): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) for (let rank = 2; rank <= 14; rank++) deck.push({ rank, suit });
  return deck;
}

function shuffleDeck(seed?: string): Card[] {
  const deck = createDeck();
  // Fisher-Yates shuffle with seeded PRNG for reproducibility
  let s = 0;
  const str = seed || String(Date.now()) + String(Math.random());
  for (let i = 0; i < str.length; i++) s = ((s << 5) - s + str.charCodeAt(i)) | 0;
  const rand = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

// ── Hand Evaluation ──────────────────────────────────────────────
type HandRank = { rank: number; name: string; tiebreak: number[] };

function evaluateBest5(cards: Card[]): HandRank {
  if (cards.length < 5) return { rank: 0, name: 'Incomplete', tiebreak: [] };
  let best: HandRank = { rank: 0, name: '', tiebreak: [] };
  // Check all 5-card combinations from 5-7 cards
  const combos = getCombinations(cards, 5);
  for (const combo of combos) {
    const h = evaluate5(combo);
    if (compareHands(h, best) > 0) best = h;
  }
  return best;
}

function getCombinations(arr: Card[], k: number): Card[][] {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const [first, ...rest] = arr;
  const withFirst = getCombinations(rest, k - 1).map(c => [first, ...c]);
  const withoutFirst = getCombinations(rest, k);
  return [...withFirst, ...withoutFirst];
}

function evaluate5(cards: Card[]): HandRank {
  const sorted = [...cards].sort((a, b) => b.rank - a.rank);
  const ranks = sorted.map(c => c.rank);
  const suits = sorted.map(c => c.suit);
  const isFlush = suits.every(s => s === suits[0]);
  const isStraight = checkStraight(ranks);
  const isWheel = ranks[0] === 14 && ranks[1] === 5 && ranks[2] === 4 && ranks[3] === 3 && ranks[4] === 2;

  // Count rank occurrences
  const counts = new Map<number, number>();
  for (const r of ranks) counts.set(r, (counts.get(r) || 0) + 1);
  const groups = [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);

  if (isFlush && isStraight) {
    if (ranks[0] === 14 && ranks[1] === 13) return { rank: 9, name: 'Royal Flush', tiebreak: [14] };
    return { rank: 8, name: 'Straight Flush', tiebreak: [isWheel ? 5 : ranks[0]] };
  }
  if (groups[0][1] === 4) return { rank: 7, name: 'Four of a Kind', tiebreak: [groups[0][0], groups[1][0]] };
  if (groups[0][1] === 3 && groups[1][1] === 2) return { rank: 6, name: 'Full House', tiebreak: [groups[0][0], groups[1][0]] };
  if (isFlush) return { rank: 5, name: 'Flush', tiebreak: ranks };
  if (isStraight || isWheel) return { rank: 4, name: 'Straight', tiebreak: [isWheel ? 5 : ranks[0]] };
  if (groups[0][1] === 3) return { rank: 3, name: 'Three of a Kind', tiebreak: [groups[0][0], ...groups.slice(1).map(g => g[0])] };
  if (groups[0][1] === 2 && groups[1][1] === 2) {
    const pairs = groups.filter(g => g[1] === 2).map(g => g[0]).sort((a, b) => b - a);
    const kicker = groups.find(g => g[1] === 1)![0];
    return { rank: 2, name: 'Two Pair', tiebreak: [...pairs, kicker] };
  }
  if (groups[0][1] === 2) return { rank: 1, name: 'One Pair', tiebreak: [groups[0][0], ...groups.slice(1).map(g => g[0])] };
  return { rank: 0, name: 'High Card', tiebreak: ranks };
}

function checkStraight(ranks: number[]): boolean {
  for (let i = 0; i < ranks.length - 1; i++) {
    if (ranks[i] - ranks[i + 1] !== 1) return false;
  }
  return true;
}

function compareHands(a: HandRank, b: HandRank): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  for (let i = 0; i < Math.min(a.tiebreak.length, b.tiebreak.length); i++) {
    if (a.tiebreak[i] !== b.tiebreak[i]) return a.tiebreak[i] - b.tiebreak[i];
  }
  return 0;
}

// ── Parse poker data from event ──────────────────────────────────
function parsePokerData(ev: any): any | null {
  try {
    const tag = ev.tags?.find((t: string[]) => t[0] === 'poker');
    if (tag?.[1]) return JSON.parse(tag[1]);
    return null;
  } catch { return null; }
}

// ── Game State Types ─────────────────────────────────────────────
type Street = 'preflop' | 'flop' | 'turn' | 'river' | 'showdown' | 'complete';
type Action = 'fold' | 'check' | 'call' | 'raise' | 'allin';

interface PokerChallenge {
  id: string;
  pubkey: string;
  createdAt: number;
  buyIn: number;
}

interface PokerGame {
  challengeId: string;
  dealer: string; // pubkey — also created the game, shuffled
  opponent: string; // pubkey
  myRole: 'dealer' | 'opponent';
  seed: string;
  deck: Card[];
  myHole: Card[];
  oppHole: Card[]; // only visible at showdown
  community: Card[];
  street: Street;
  pot: number;
  myChips: number;
  oppChips: number;
  myBet: number;
  oppBet: number;
  toAct: 'dealer' | 'opponent';
  actions: { by: string; action: Action; amount?: number }[];
  winner: string | null;
  winnerHand: string | null;
}

// ── Session persistence for active games ────────────────────────
// Saves the active game to sessionStorage so a page refresh can
// rejoin instead of dumping the player back to the lobby.
const GAME_SESSION_KEY = 'bchnostr:poker-active-game';

function saveGameToSession(game: PokerGame | null) {
  try {
    if (!game) { sessionStorage.removeItem(GAME_SESSION_KEY); return; }
    // Serialize the game — Cards are plain objects so JSON is fine
    sessionStorage.setItem(GAME_SESSION_KEY, JSON.stringify(game));
  } catch {}
}

function loadGameFromSession(): PokerGame | null {
  try {
    const raw = sessionStorage.getItem(GAME_SESSION_KEY);
    if (!raw) return null;
    const game = JSON.parse(raw) as PokerGame;
    // Sanity check — must have essential fields and not be too old (6 hours)
    if (!game.challengeId || !game.dealer || !game.opponent) return null;
    return game;
  } catch { return null; }
}

// ══════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════════

export default function PokerPage() {
  const { user } = useAppContext();
  const [view, setView] = useState<'lobby' | 'game'>(() => {
    // Restore game view on mount if there's a saved session
    const saved = loadGameFromSession();
    return saved ? 'game' : 'lobby';
  });
  const [activeGame, setActiveGame] = useState<PokerGame | null>(() => loadGameFromSession());

  const startGame = useCallback((game: PokerGame) => {
    setActiveGame(game);
    setView('game');
    saveGameToSession(game);
  }, []);

  const backToLobby = useCallback(() => {
    setActiveGame(null);
    setView('lobby');
    saveGameToSession(null);
  }, []);

  // Persist game state changes to session storage
  const setGameWithPersist = useCallback((game: PokerGame | null) => {
    setActiveGame(game);
    saveGameToSession(game);
  }, []);

  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          {view === 'game' ? (
            <button onClick={backToLobby} className="p-1 rounded-lg hover:bg-muted -ml-1"><ChevronLeft className="w-5 h-5" /></button>
          ) : (
            <Link to="/games" className="p-1 rounded-lg hover:bg-muted -ml-1"><ChevronLeft className="w-5 h-5" /></Link>
          )}
          <h1 className="text-lg font-bold flex items-center gap-2">
            <span className="text-xl">🃏</span> BCH Poker
          </h1>
        </div>
      </div>

      {view === 'lobby' && <Lobby user={user} onStartGame={startGame} />}
      {view === 'game' && activeGame && <GameView game={activeGame} setGame={setGameWithPersist} onBack={backToLobby} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  LOBBY
// ══════════════════════════════════════════════════════════════════

function Lobby({ user, onStartGame }: { user: any; onStartGame: (g: PokerGame) => void }) {
  const { publish } = useNostrPublish();
  const [creating, setCreating] = useState(false);
  const joinedRef = useRef(false); // prevent double-join

  const [since] = useState(() => Math.floor(Date.now() / 1000) - 3600 * 6);
  const { events: challengeEvents, loading, refetch: refetchC } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [POKER_TAG, 'challenge'], since, limit: 50 }],
    allRelays: true,
  });
  const { events: acceptEvents, refetch: refetchA } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [POKER_TAG, 'accept'], since, limit: 50 }],
    allRelays: true,
  });

  // Poll lobby — faster when user has an open challenge (waiting for opponent)
  const hasOwnChallenge = useMemo(() => {
    if (!user) return false;
    return challengeEvents.some(e => {
      const d = parsePokerData(e);
      return d?.type === 'challenge' && e.pubkey === user.pubkey;
    });
  }, [challengeEvents.length, user?.pubkey]);

  useEffect(() => {
    // Poll faster (3s) when waiting for someone to join your table
    const interval = hasOwnChallenge ? 3000 : 10000;
    const iv = setInterval(() => { refetchC(); refetchA(); }, interval);
    return () => clearInterval(iv);
  }, [refetchC, refetchA, hasOwnChallenge]);

  // Map of challengeId → accept event data
  const acceptMap = useMemo(() => {
    const map = new Map<string, { event: any; data: any }>();
    for (const e of acceptEvents) {
      const d = parsePokerData(e);
      if (d?.challengeId && d?.type === 'accept') {
        map.set(d.challengeId, { event: e, data: d });
      }
    }
    return map;
  }, [acceptEvents.length]);

  const acceptedIds = useMemo(() => new Set(acceptMap.keys()), [acceptMap]);

  // ── AUTO-JOIN: When someone accepts YOUR challenge, enter the game ──
  // This is the critical fix for Bug #2. The dealer (challenge creator)
  // never had a mechanism to enter the game — the accept event just made
  // their challenge disappear from the lobby.
  useEffect(() => {
    if (!user || joinedRef.current) return;

    // Find MY challenges
    for (const e of challengeEvents) {
      if (e.pubkey !== user.pubkey) continue;
      const cd = parsePokerData(e);
      if (!cd || cd.type !== 'challenge') continue;

      // Check if this challenge has been accepted
      const accept = acceptMap.get(e.id);
      if (!accept) continue;

      const { data: ad } = accept;
      if (ad.dealer !== user.pubkey) continue; // sanity

      // Someone accepted! Build game state for the dealer (me)
      const dealerHole = (ad.dealerHole || []).map(parseCardKey).filter(Boolean) as Card[];
      const oppHole = (ad.oppHole || []).map(parseCardKey).filter(Boolean) as Card[];
      const community = (ad.community || []).map(parseCardKey).filter(Boolean) as Card[];

      if (dealerHole.length < 2 || oppHole.length < 2 || community.length < 5) continue;

      const deck = shuffleDeck(ad.seed || '');

      joinedRef.current = true;
      onStartGame({
        challengeId: e.id,
        dealer: user.pubkey,
        opponent: ad.opponent,
        myRole: 'dealer',
        seed: ad.seed || '',
        deck,
        myHole: dealerHole,
        oppHole: oppHole,
        community,
        street: 'preflop',
        pot: SMALL_BLIND + BIG_BLIND,
        myChips: STARTING_CHIPS - SMALL_BLIND, // dealer posts small blind
        oppChips: STARTING_CHIPS - BIG_BLIND, // opponent posts big blind
        myBet: SMALL_BLIND,
        oppBet: BIG_BLIND,
        toAct: 'dealer', // dealer acts first preflop
        actions: [],
        winner: null,
        winnerHand: null,
      });
      return; // only join one game
    }
  }, [user?.pubkey, challengeEvents.length, acceptMap, onStartGame]);

  const challenges = useMemo(() => {
    const list: PokerChallenge[] = [];
    for (const e of challengeEvents) {
      const d = parsePokerData(e);
      if (!d || d.type !== 'challenge') continue;
      if (acceptedIds.has(e.id)) continue;
      list.push({ id: e.id, pubkey: e.pubkey, createdAt: e.created_at, buyIn: d.buyIn || STARTING_CHIPS });
    }
    return list.sort((a, b) => b.createdAt - a.createdAt);
  }, [challengeEvents.length, acceptedIds.size]);

  const handleCreate = async () => {
    if (!user || creating) return;
    setCreating(true);
    try {
      await publish({
        kind: 1,
        content: '🃏 Looking for a poker opponent! Heads-up Texas Hold\'em on bchnostr.com/games/poker',
        tags: [['t', POKER_TAG], ['t', 'challenge'], ['poker', JSON.stringify({ type: 'challenge', buyIn: STARTING_CHIPS })]],
        created_at: Math.floor(Date.now() / 1000),
      });
      setTimeout(refetchC, 2000);
    } catch {}
    setCreating(false);
  };

  const handleAccept = async (ch: PokerChallenge) => {
    if (!user || ch.pubkey === user.pubkey) return;

    // Challenger is dealer (they shuffle), acceptor is opponent
    const seed = `${ch.id}-${user.pubkey}-${Date.now()}`;
    const deck = shuffleDeck(seed);

    // Deal: dealer gets cards 0,1 — opponent gets 2,3 — community 4-8
    const dealerHole = [deck[0], deck[1]];
    const oppHole = [deck[2], deck[3]];
    const community = [deck[4], deck[5], deck[6], deck[7], deck[8]];

    try {
      const acceptData = {
        type: 'accept',
        challengeId: ch.id,
        dealer: ch.pubkey,
        opponent: user.pubkey,
        seed,
        // Send full deal — trust-based (like chess, opponent could read events)
        dealerHole: dealerHole.map(cardKey),
        oppHole: oppHole.map(cardKey),
        community: community.map(cardKey),
      };

      await publish({
        kind: 1,
        content: '🃏 Poker challenge accepted! Game on!',
        tags: [
          ['t', POKER_TAG], ['t', 'accept'],
          ['e', ch.id], ['p', ch.pubkey],
          ['poker', JSON.stringify(acceptData)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // I'm the opponent (acceptor)
      joinedRef.current = true;
      onStartGame({
        challengeId: ch.id,
        dealer: ch.pubkey,
        opponent: user.pubkey,
        myRole: 'opponent',
        seed,
        deck,
        myHole: oppHole,
        oppHole: dealerHole,
        community,
        street: 'preflop',
        pot: SMALL_BLIND + BIG_BLIND,
        myChips: STARTING_CHIPS - BIG_BLIND, // opponent posts big blind
        oppChips: STARTING_CHIPS - SMALL_BLIND, // dealer posts small blind
        myBet: BIG_BLIND,
        oppBet: SMALL_BLIND,
        toAct: 'dealer', // dealer acts first preflop
        actions: [],
        winner: null,
        winnerHand: null,
      });
    } catch {}
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      <div className="text-center py-6">
        <div className="text-5xl mb-3">🃏</div>
        <h2 className="text-xl font-bold">BCH Poker</h2>
        <p className="text-sm text-muted-foreground mt-1">Heads-up Texas Hold'em over Nostr</p>
      </div>

      {user ? (
        <button onClick={handleCreate} disabled={creating}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create Table
        </button>
      ) : (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">Log in to play poker</p>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
          🃏 Open Tables {loading && <Loader2 className="w-3 h-3 animate-spin" />}
        </h3>
        {challenges.length === 0 && !loading && (
          <div className="text-center py-8 px-4">
            <p className="text-sm text-muted-foreground">No open tables</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Create one and wait for an opponent!</p>
          </div>
        )}
        <div className="space-y-2">
          {challenges.map(c => (
            <ChallengeCard key={c.id} ch={c} isOwn={c.pubkey === user?.pubkey}
              onAccept={() => handleAccept(c)} canAccept={!!user && c.pubkey !== user?.pubkey} />
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-muted/50 border border-border p-4 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">How it works</p>
        <ul className="text-xs text-muted-foreground space-y-1.5">
          <li className="flex items-start gap-2"><span className="text-primary font-bold">1.</span> Create a table or join someone else's</li>
          <li className="flex items-start gap-2"><span className="text-primary font-bold">2.</span> Play heads-up Texas Hold'em — bet, raise, or fold</li>
          <li className="flex items-start gap-2"><span className="text-primary font-bold">3.</span> Every action is published to Nostr in real-time</li>
          <li className="flex items-start gap-2"><span className="text-primary font-bold">4.</span> Best hand wins! Tip your opponent with BCH</li>
        </ul>
        <p className="text-[10px] text-muted-foreground/60 mt-2">
          Play chips only — no real money at stake. {STARTING_CHIPS.toLocaleString()} chips per player, {SMALL_BLIND}/{BIG_BLIND} blinds.
        </p>
      </div>
    </div>
  );
}

function ChallengeCard({ ch, isOwn, onAccept, canAccept }: {
  ch: PokerChallenge; isOwn: boolean; onAccept: () => void; canAccept: boolean;
}) {
  const { profile } = useNostrProfile(ch.pubkey);
  const name = profile?.display_name || profile?.name || ch.pubkey.slice(0, 8) + '...';
  return (
    <div className={`rounded-xl border p-4 flex items-center justify-between ${isOwn ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}>
      <div className="flex items-center gap-3">
        <Link to={`/profile/${ch.pubkey}`}>
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"><User className="w-4 h-4 text-muted-foreground" /></div>
          )}
        </Link>
        <div>
          <p className="text-sm font-semibold">{name}</p>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Coins className="w-3 h-3" /> {ch.buyIn} chips
            <span className="mx-1">&middot;</span>
            <Clock className="w-3 h-3" /> {timeAgo(ch.createdAt)}
          </p>
        </div>
      </div>
      {isOwn ? (
        <span className="text-xs text-primary flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          Waiting...
        </span>
      ) : canAccept ? (
        <button onClick={onAccept} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-xs hover:bg-primary/90 transition-colors">
          Join
        </button>
      ) : null}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  GAME VIEW
// ══════════════════════════════════════════════════════════════════

function GameView({ game, setGame, onBack }: {
  game: PokerGame; setGame: (g: PokerGame | null) => void; onBack: () => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [raiseAmount, setRaiseAmount] = useState(BIG_BLIND * 2);
  const [publishing, setPublishing] = useState(false);
  const [showCards, setShowCards] = useState(false);

  const opponentPubkey = game.myRole === 'dealer' ? game.opponent : game.dealer;
  const { profile: oppProfile } = useNostrProfile(opponentPubkey);
  const oppName = oppProfile?.display_name || oppProfile?.name || opponentPubkey.slice(0, 8) + '...';
  const isMyTurn = game.toAct === game.myRole;

  // ── Listen for opponent actions ────────────────────────────────
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 3600 * 6);
  const { events: actionEvents, refetch } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [POKER_TAG, 'action'], '#e': [game.challengeId], since, limit: 200 }],
    allRelays: true,
  });

  // Poll for actions
  useEffect(() => {
    if (game.winner) return;
    const iv = setInterval(refetch, isMyTurn ? 8000 : 3000);
    return () => clearInterval(iv);
  }, [isMyTurn, game.winner, refetch]);

  // Rebuild from action events
  useEffect(() => {
    const allActions = actionEvents
      .filter(e => { const d = parsePokerData(e); return d?.type === 'action' && d?.gameId === game.challengeId; })
      .sort((a, b) => a.created_at - b.created_at);

    if (allActions.length <= game.actions.length) return;

    // Rebuild game from actions
    let g = { ...game, actions: [] as any[], street: 'preflop' as Street,
      pot: SMALL_BLIND + BIG_BLIND,
      myChips: game.myRole === 'opponent' ? STARTING_CHIPS - BIG_BLIND : STARTING_CHIPS - SMALL_BLIND,
      oppChips: game.myRole === 'opponent' ? STARTING_CHIPS - SMALL_BLIND : STARTING_CHIPS - BIG_BLIND,
      myBet: game.myRole === 'opponent' ? BIG_BLIND : SMALL_BLIND,
      oppBet: game.myRole === 'opponent' ? SMALL_BLIND : BIG_BLIND,
      toAct: 'dealer' as 'dealer' | 'opponent',
      winner: null as string | null,
      winnerHand: null as string | null,
    };

    for (const e of allActions) {
      const d = parsePokerData(e);
      if (!d?.action) continue;
      g = applyAction(g, d.by, d.action, d.amount);
    }

    setGame(g);
  }, [actionEvents.length]);

  // Also listen for deal events (when dealer joins a game someone else created)
  const { events: dealEvents, refetch: refetchDeal } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [POKER_TAG, 'deal'], '#e': [game.challengeId], since, limit: 5 }],
    allRelays: true,
  });

  useEffect(() => {
    if (game.myHole.length > 0) return; // already have cards
    for (const e of dealEvents) {
      const d = parsePokerData(e);
      if (d?.type === 'deal' && d?.gameId === game.challengeId) {
        // Parse cards for my role
        const myCards = game.myRole === 'dealer' ? d.dealerHole : d.oppHole;
        const oppCards = game.myRole === 'dealer' ? d.oppHole : d.dealerHole;
        if (myCards && oppCards) {
          setGame({
            ...game,
            myHole: myCards.map(parseCardKey).filter(Boolean) as Card[],
            oppHole: oppCards.map(parseCardKey).filter(Boolean) as Card[],
            community: (d.community || []).map(parseCardKey).filter(Boolean) as Card[],
          });
        }
      }
    }
  }, [dealEvents.length]);

  useEffect(() => {
    const iv = setInterval(refetchDeal, 5000);
    return () => clearInterval(iv);
  }, [refetchDeal]);

  // ── Make action ────────────────────────────────────────────────
  const doAction = useCallback(async (action: Action, amount?: number) => {
    if (!user || !isMyTurn || game.winner || publishing) return;
    setPublishing(true);

    const newGame = applyAction(game, game.myRole, action, amount);
    setGame(newGame);

    try {
      await publish({
        kind: 1,
        content: `🃏 ${action}${amount ? ` ${amount}` : ''}`,
        tags: [
          ['t', POKER_TAG], ['t', 'action'],
          ['e', game.challengeId], ['p', opponentPubkey],
          ['poker', JSON.stringify({ type: 'action', gameId: game.challengeId, by: game.myRole, action, amount })],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setTimeout(refetch, 1000);
    } catch {}
    setPublishing(false);
  }, [user, isMyTurn, game, publishing, publish, opponentPubkey, refetch]);

  // ── Visible community cards based on street ────────────────────
  const visibleCommunity = useMemo(() => {
    if (game.street === 'preflop') return [];
    if (game.street === 'flop') return game.community.slice(0, 3);
    if (game.street === 'turn') return game.community.slice(0, 4);
    return game.community.slice(0, 5); // river, showdown, complete
  }, [game.street, game.community]);

  const isShowdown = game.street === 'showdown' || game.street === 'complete';

  // My hand evaluation
  const myHand = useMemo(() => {
    if (game.myHole.length < 2) return null;
    const all = [...game.myHole, ...visibleCommunity];
    if (all.length < 5) return null;
    return evaluateBest5(all);
  }, [game.myHole, visibleCommunity]);

  const callAmount = Math.abs(game.oppBet - game.myBet);
  const canCheck = game.myBet >= game.oppBet;
  const minRaise = Math.max(BIG_BLIND, callAmount * 2);

  return (
    <div className="max-w-lg mx-auto p-4 space-y-3">
      {/* Opponent */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2">
          {oppProfile?.picture ? (
            <img src={oppProfile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><User className="w-4 h-4 text-muted-foreground" /></div>
          )}
          <div>
            <p className="text-xs font-semibold">{oppName}</p>
            <p className="text-[10px] text-muted-foreground"><Coins className="w-3 h-3 inline" /> {game.oppChips} chips</p>
          </div>
        </div>
        {!isMyTurn && !game.winner && <span className="text-[10px] text-primary flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-primary animate-pulse" /> thinking</span>}
      </div>

      {/* Opponent hole cards */}
      <div className="flex justify-center gap-2">
        {isShowdown ? (
          game.oppHole.map((c, i) => <CardDisplay key={i} card={c} />)
        ) : (
          <>
            <CardBack /><CardBack />
          </>
        )}
      </div>

      {/* Table — community cards + pot */}
      <div className="rounded-2xl bg-emerald-800/30 dark:bg-emerald-900/40 border border-emerald-700/30 p-5 space-y-3">
        {/* Pot */}
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/20 text-emerald-300 text-xs font-bold">
            <Coins className="w-3.5 h-3.5" /> Pot: {game.pot}
          </span>
        </div>

        {/* Community cards */}
        <div className="flex justify-center gap-2 min-h-[72px]">
          {visibleCommunity.map((c, i) => <CardDisplay key={i} card={c} />)}
          {/* Placeholder slots */}
          {Array.from({ length: 5 - visibleCommunity.length }).map((_, i) => (
            <div key={`empty-${i}`} className="w-12 h-[68px] rounded-lg border border-emerald-600/20 bg-emerald-900/20" />
          ))}
        </div>

        {/* Street label */}
        <p className="text-center text-[10px] text-emerald-400/60 uppercase tracking-widest font-bold">
          {game.street === 'complete' ? (game.winner ? 'Winner!' : 'Complete') : game.street}
        </p>
      </div>

      {/* My hole cards */}
      <div className="flex justify-center gap-2">
        {game.myHole.length > 0 ? (
          showCards || true ? (
            game.myHole.map((c, i) => <CardDisplay key={i} card={c} highlight />)
          ) : (
            <><CardBack /><CardBack /></>
          )
        ) : (
          <p className="text-xs text-muted-foreground py-4">Waiting for deal...</p>
        )}
      </div>

      {/* Hand name */}
      {myHand && (
        <p className="text-center text-xs font-bold text-primary">{myHand.name}</p>
      )}

      {/* Me */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-xs font-semibold">You</p>
            <p className="text-[10px] text-muted-foreground"><Coins className="w-3 h-3 inline" /> {game.myChips} chips</p>
          </div>
        </div>
        {isMyTurn && !game.winner && <span className="text-[10px] text-primary font-medium">Your turn</span>}
      </div>

      {/* ── Actions ──────────────────────────────────────────── */}
      {!game.winner && isMyTurn && (
        <div className="space-y-2 pt-1">
          <div className="flex gap-2">
            <button onClick={() => doAction('fold')} disabled={publishing}
              className="flex-1 px-3 py-2.5 rounded-xl border border-red-500/30 text-red-500 text-sm font-medium hover:bg-red-500/10 disabled:opacity-50 transition-colors">
              Fold
            </button>
            {canCheck ? (
              <button onClick={() => doAction('check')} disabled={publishing}
                className="flex-1 px-3 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted disabled:opacity-50 transition-colors">
                Check
              </button>
            ) : (
              <button onClick={() => doAction('call', callAmount)} disabled={publishing || game.myChips < callAmount}
                className="flex-1 px-3 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
                Call {callAmount}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-1.5">
              <button onClick={() => setRaiseAmount(a => Math.max(minRaise, a - BIG_BLIND))}
                className="p-1.5 rounded-lg border border-border hover:bg-muted"><Minus className="w-3.5 h-3.5" /></button>
              <span className="flex-1 text-center text-sm font-mono font-bold">{raiseAmount}</span>
              <button onClick={() => setRaiseAmount(a => Math.min(game.myChips, a + BIG_BLIND))}
                className="p-1.5 rounded-lg border border-border hover:bg-muted"><ArrowUp className="w-3.5 h-3.5" /></button>
            </div>
            <button onClick={() => doAction('raise', raiseAmount)} disabled={publishing || game.myChips < raiseAmount}
              className="px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 disabled:opacity-50 transition-colors">
              Raise
            </button>
          </div>
          <button onClick={() => doAction('allin', game.myChips)} disabled={publishing}
            className="w-full px-3 py-2 rounded-xl border-2 border-amber-500/30 text-amber-500 text-sm font-bold hover:bg-amber-500/10 disabled:opacity-50 transition-colors">
            All In ({game.myChips})
          </button>
        </div>
      )}

      {/* ── Result ────────────────────────────────────────────── */}
      {game.winner && (
        <div className="rounded-2xl border-2 border-primary/30 bg-card p-5 text-center space-y-3">
          {game.winner === user?.pubkey ? (
            <Trophy className="w-10 h-10 text-amber-500 mx-auto" />
          ) : game.winner === 'split' ? (
            <Coins className="w-10 h-10 text-muted-foreground mx-auto" />
          ) : (
            <Flag className="w-10 h-10 text-red-500 mx-auto" />
          )}
          <p className="text-sm font-bold">
            {game.winner === user?.pubkey ? 'You won!' : game.winner === 'split' ? 'Split pot!' : `${oppName} wins!`}
          </p>
          {game.winnerHand && <p className="text-xs text-muted-foreground">{game.winnerHand}</p>}

          {user && opponentPubkey && (
            <div className="flex flex-col items-center gap-1.5 pt-1">
              <p className="text-[10px] text-muted-foreground">Good game? Tip your opponent!</p>
              <BCHTipButton recipientPubkey={opponentPubkey} recipientProfile={oppProfile} />
            </div>
          )}

          <button onClick={onBack} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-xs hover:bg-primary/90 transition-colors">
            Back to Lobby
          </button>
        </div>
      )}

      {/* Waiting for opponent */}
      {!game.winner && !isMyTurn && (
        <div className="flex items-center justify-center gap-2 py-2">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Waiting for {oppName}...</span>
          <button onClick={refetch} className="p-1.5 rounded-lg hover:bg-muted"><RefreshCw className="w-3.5 h-3.5 text-muted-foreground" /></button>
        </div>
      )}

      {/* Action log */}
      {game.actions.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Actions</p>
          <div className="flex flex-wrap gap-1.5">
            {game.actions.map((a, i) => (
              <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                a.action === 'fold' ? 'bg-red-500/10 text-red-500'
                : a.action === 'raise' || a.action === 'allin' ? 'bg-amber-500/10 text-amber-600'
                : 'bg-muted text-muted-foreground'
              }`}>
                {a.by === game.myRole ? 'You' : oppName.split(' ')[0]}: {a.action}{a.amount ? ` ${a.amount}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Card Components ──────────────────────────────────────────────

function CardDisplay({ card, highlight }: { card: Card; highlight?: boolean }) {
  return (
    <div className={`w-12 h-[68px] rounded-lg border-2 flex flex-col items-center justify-center text-sm font-bold shadow-sm ${
      highlight ? 'border-primary/40 bg-card' : 'border-border bg-card'
    }`}>
      <span className={SUIT_COLOR[card.suit]}>{RANK_DISPLAY[card.rank]}</span>
      <span className={`text-lg leading-none ${SUIT_COLOR[card.suit]}`}>{SUIT_DISPLAY[card.suit]}</span>
    </div>
  );
}

function CardBack() {
  return (
    <div className="w-12 h-[68px] rounded-lg border-2 border-blue-800/30 bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-sm">
      <span className="text-blue-300/40 text-lg font-bold">?</span>
    </div>
  );
}

// ── Game Logic ───────────────────────────────────────────────────

function applyAction(game: PokerGame, by: string, action: Action, amount?: number): PokerGame {
  const g = { ...game, actions: [...game.actions, { by, action, amount }] };
  const isMy = by === game.myRole;

  if (action === 'fold') {
    const winnerId = isMy
      ? (game.myRole === 'dealer' ? game.opponent : game.dealer)
      : (game.myRole === 'dealer' ? game.dealer : game.opponent);
    return { ...g, winner: winnerId, winnerHand: 'Opponent folded', street: 'complete' };
  }

  if (action === 'call') {
    const callAmt = amount || Math.abs(g.oppBet - g.myBet);
    if (isMy) {
      g.myChips -= callAmt;
      g.myBet += callAmt;
      g.pot += callAmt;
    } else {
      g.oppChips -= callAmt;
      g.oppBet += callAmt;
      g.pot += callAmt;
    }
    // After a call, advance street
    return advanceStreet(g);
  }

  if (action === 'check') {
    // If both checked (second check), advance street
    const lastAction = g.actions[g.actions.length - 2];
    if (lastAction?.action === 'check') {
      return advanceStreet(g);
    }
    // Switch turn
    g.toAct = g.toAct === 'dealer' ? 'opponent' : 'dealer';
    return g;
  }

  if (action === 'raise' || action === 'allin') {
    const raiseAmt = amount || BIG_BLIND * 2;
    if (isMy) {
      g.myChips -= raiseAmt;
      g.myBet += raiseAmt;
      g.pot += raiseAmt;
    } else {
      g.oppChips -= raiseAmt;
      g.oppBet += raiseAmt;
      g.pot += raiseAmt;
    }
    g.toAct = g.toAct === 'dealer' ? 'opponent' : 'dealer';
    return g;
  }

  return g;
}

function advanceStreet(game: PokerGame): PokerGame {
  const g = { ...game, myBet: 0, oppBet: 0 };
  // Post-flop, opponent acts first (dealer has position)
  g.toAct = 'opponent';

  if (g.street === 'preflop') {
    g.street = 'flop';
  } else if (g.street === 'flop') {
    g.street = 'turn';
  } else if (g.street === 'turn') {
    g.street = 'river';
  } else if (g.street === 'river') {
    // Showdown
    return resolveShowdown(g);
  }
  return g;
}

function resolveShowdown(game: PokerGame): PokerGame {
  const g = { ...game, street: 'showdown' as Street };
  const myAll = [...g.myHole, ...g.community];
  const oppAll = [...g.oppHole, ...g.community];

  if (myAll.length < 5 || oppAll.length < 5) {
    return { ...g, street: 'complete', winner: 'split', winnerHand: 'Incomplete hand' };
  }

  const myHand = evaluateBest5(myAll);
  const oppHand = evaluateBest5(oppAll);
  const cmp = compareHands(myHand, oppHand);

  const myPubkey = game.myRole === 'dealer' ? game.dealer : game.opponent;
  const oppPubkey = game.myRole === 'dealer' ? game.opponent : game.dealer;

  if (cmp > 0) {
    return { ...g, street: 'complete', winner: myPubkey, winnerHand: `${myHand.name} beats ${oppHand.name}` };
  } else if (cmp < 0) {
    return { ...g, street: 'complete', winner: oppPubkey, winnerHand: `${oppHand.name} beats ${myHand.name}` };
  } else {
    return { ...g, street: 'complete', winner: 'split', winnerHand: `Split — both have ${myHand.name}` };
  }
}

function parseCardKey(key: string): Card | null {
  // e.g. "14h" → { rank: 14, suit: 'h' }
  const match = key.match(/^(\d+)([hdcs])$/);
  if (!match) return null;
  return { rank: parseInt(match[1]), suit: match[2] as Suit };
}

